import { Tabs, Select, Button, Collapse, Tag, Pagination, Dropdown, Space, Input } from 'antd';
import type { GetProps } from 'antd';
import type { TabsProps } from 'antd';
import type { MenuProps } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import {
  activePanelAC,
  repeatInterbankTransactionAC,
  repeatInternalTransactionAC,
  repeatOwnTransactionAC,
  repeatSwiftRubTransactionAC,
  repeatSwiftTransactionAC,
} from '@store/reducers/transactions';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { deleteTemplateApi, getTemplatesApi, getTemplatesDownloadApi } from '@http/user';
import { useNavigate } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';

type SearchProps = GetProps<typeof Input.Search>;
const { Panel } = Collapse;
const { Search } = Input;

function Templates() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const templates = useAppSelector(state => state.userReducer.templates);
  const activePanel = useAppSelector(state => state.transactionsReducer.activePanel);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState(templates);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTemplates = filteredTemplates.slice(startIndex, endIndex);

  const onChange = (type: string) => {
    dispatch(getTemplatesApi({ transaction_type: type }));
    setCurrentPage(1);
    setSearchQuery('');
  };

  const openPanel = (key: any) => {
    dispatch(activePanelAC(key));
  };

  const items: TabsProps['items'] = [
    {
      key: 'INTERNAL',
      label: t('Internal'),
    },
    {
      key: 'INTERBANK',
      label: t('Interbank'),
    },
    {
      key: 'SWIFT',
      label: t('International'),
    },
    {
      key: 'SWIFT_RUB',
      label: t('Ruble transfer'),
    },
  ];

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const onSearch: SearchProps['onSearch'] = value => {
    setSearchQuery(value);
  };

  useEffect(() => {
    dispatch(activePanelAC(''));
    dispatch(getTemplatesApi({ transaction_type: 'INTERNAL' }));
  }, []);

  useEffect(() => {
    const filtered = templates.filter(template =>
      (template.title?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  }, [templates, searchQuery]);

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[1130px] w-full'>
        <div className='flex'>
          <div className='p-4 lg:p-8 w-full h-full bg-white mb-12'>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h1 className='max-md:hidden text-2xl mb-2 font-semibold color-active'>{t('Templates')}</h1>
                <div>
                  <Search
                    placeholder={`${t('Search')}`}
                    onSearch={onSearch}
                    onChange={e => setSearchQuery(e.target.value)}
                    value={searchQuery}
                    className=' max-md:hidden w-[350px] mr-[15px]'
                    allowClear
                  />
                  <Button className='max-md:hidden' type='primary' onClick={() => dispatch(getTemplatesDownloadApi())}>
                    {t('downloadTemplates')}
                  </Button>
                </div>
              </div>

              <div className='flex justify-between md:hidden'>
                <h1 className='text-xl lg:text-2xl font-semibold color-active'>{t('Templates')}</h1>
                <div style={{ alignItems: 'center', display: 'flex', columnGap: '10px' }}>
                  <Select
                    style={{ width: 160 }}
                    allowClear
                    placeholder={`${t('templateType')}`}
                    onChange={value => onChange(value || 'INTERNAL')} // Используем существующую функцию onChange
                  >
                    <Select.Option value='INTERNAL'>{t('Internal')}</Select.Option>
                    <Select.Option value='INTERBANK'>{t('Interbank')}</Select.Option>
                    <Select.Option value='SWIFT'>{t('International')}</Select.Option>
                    <Select.Option value='SWIFT_RUB'>{t('Ruble transfer')}</Select.Option>
                  </Select>
                  <Button type='primary' onClick={() => dispatch(getTemplatesDownloadApi())}>
                    <DownloadOutlined style={{ fontSize: '20px' }} />
                  </Button>
                </div>
              </div>
              <div className='md:hidden mt-5'>
                <Input
                  onChange={e => setSearchQuery(e.target.value)}
                  value={searchQuery}
                  placeholder={`${t('Search')}`}
                  variant='borderless'
                  size='large'
                />
              </div>
              <Tabs
                className='max-md:hidden color-active font-medium'
                defaultActiveKey=''
                items={items}
                onChange={onChange}
              />
              <div className='py-5'>
                {filteredTemplates.length > 0 ? (
                  <>
                    <Collapse activeKey={activePanel} onChange={openPanel} ghost className='p-0'>
                      {currentTemplates?.map((item: any) => (
                        <Panel
                          header={
                            <div className='flex justify-between items-center max-md:p-[10px] p-5 border-b cursor-pointer'>
                              <div className='flex justify-between items-center'>
                                <div>
                                  <p className=' max-md:text-[12px] md:w-[500px]  mb-[10px]'>{item?.title}</p>
                                  <span className='md:hidden' style={{ fontSize: '10px' }}>
                                    {item?.visibility === 'P' ? (
                                      <Tag color='processing'>{t('Personal')}</Tag>
                                    ) : item?.visibility === 'C' ? (
                                      <Tag color='processing'>{t('Public')}</Tag>
                                    ) : (
                                      ''
                                    )}
                                  </span>
                                </div>
                              </div>

                              <div className='flex flex-wrap gap-y-[10px]'>
                                <span className='ml-3 max-md:hidden' style={{ fontSize: '10px' }}>
                                  {item?.visibility === 'P' ? (
                                    <Tag color='processing'>{t('Personal')}</Tag>
                                  ) : item?.visibility === 'C' ? (
                                    <Tag color='processing'>{t('Public')}</Tag>
                                  ) : (
                                    ''
                                  )}
                                </span>

                                <div
                                  className='md:hidden '
                                  style={{ display: 'flex', flexWrap: 'wrap', rowGap: '10px' }}
                                >
                                  <Dropdown
                                    className='md:hidden'
                                    menu={
                                      {
                                        items: [
                                          {
                                            key: 'make_payment',
                                            label: t('Make a payment'),
                                            onClick: () => {
                                              navigate(`/payments/${item.transaction_type.toLowerCase()}`);
                                              if (item.transaction_type === 'OWN') {
                                                dispatch(repeatOwnTransactionAC(item));
                                              } else if (item.transaction_type === 'INTERNAL') {
                                                dispatch(repeatInternalTransactionAC(item));
                                              } else if (item.transaction_type === 'INTERBANK') {
                                                dispatch(repeatInterbankTransactionAC(item));
                                              } else if (item.transaction_type === 'SWIFT') {
                                                dispatch(repeatSwiftTransactionAC(item));
                                              } else if (item.transaction_type === 'SWIFT_RUB') {
                                                dispatch(repeatSwiftRubTransactionAC(item));
                                              }
                                            },
                                          },
                                          {
                                            key: 'delete',
                                            label: <span style={{ color: '#ff4d4f' }}>{t('Delete')}</span>,
                                            onClick: e => {
                                              e.domEvent.stopPropagation();
                                              dispatch(deleteTemplateApi({ id: item?.id }, item?.transaction_type));
                                              dispatch(activePanelAC({}));
                                            },
                                          },
                                        ],
                                      } as MenuProps
                                    }
                                    placement='bottomRight'
                                    trigger={['click']}
                                  >
                                    <div className='ml-3 text-[25px]' onClick={e => e.stopPropagation()}>
                                      <Space>{t('...')}</Space>
                                    </div>
                                  </Dropdown>
                                </div>

                                <div className='max-md:hidden max-md:w-[100px]'>
                                  <Button
                                    type='primary'
                                    size='small'
                                    className=' ml-3  max-md:text-[12px]'
                                    danger
                                    onClick={e => {
                                      dispatch(deleteTemplateApi({ id: item?.id }, item?.transaction_type));
                                      dispatch(activePanelAC({}));
                                      e.stopPropagation();
                                    }}
                                  >
                                    {t('Delete')}
                                  </Button>
                                  <Button
                                    className='ml-3  max-md:text-[12px]'
                                    size='small'
                                    type='primary'
                                    onClick={() => {
                                      navigate(`/payments/${item.transaction_type.toLowerCase()}`);
                                      if (item.transaction_type === 'OWN') {
                                        dispatch(repeatOwnTransactionAC(item));
                                      } else if (item.transaction_type === 'INTERNAL') {
                                        dispatch(repeatInternalTransactionAC(item));
                                      } else if (item.transaction_type === 'INTERBANK') {
                                        dispatch(repeatInterbankTransactionAC(item));
                                      } else if (item.transaction_type === 'SWIFT') {
                                        dispatch(repeatSwiftTransactionAC(item));
                                      } else if (item.transaction_type === 'SWIFT_RUB') {
                                        dispatch(repeatSwiftRubTransactionAC(item));
                                      }
                                    }}
                                  >
                                    {t('Make a payment')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          }
                          key={item?.id}
                        >
                          <div className='bg-slate-50'>
                            <div className='grid grid-cols-7 items-center py-5 max-sm:px-8 px-2 border-b lg:gap-5 max-sm:text-xs'>
                              <div />
                              <div className='max-sm:col-span-7 max-sm:leading-6 col-span-5 leading-8'>
                                <div className='grid grid-cols-6'>
                                  <p className='col-span-2'>{t('Date of creation')}: </p>
                                  <p className='ml-3 font-bold col-span-4'>{item?.created_at}</p>
                                </div>
                                <div className='grid grid-cols-6'>
                                  <p className='col-span-2'>{t('Sender')}: </p>
                                  <p className='ml-3 font-bold col-span-4'>{item?.sending_ac}</p>
                                </div>
                                <div className='grid grid-cols-6'>
                                  <p className='col-span-2'>{t('Recipient')}: </p>
                                  <p className='ml-3 font-bold col-span-4'>{item?.receiving_ac}</p>
                                </div>
                                <div className='grid grid-cols-6'>
                                  <p className='col-span-2'>{t('Amount')}: </p>
                                  <p className='ml-3 font-bold col-span-4'>
                                    {item?.sending_amount} {item?.sending_ccy}
                                  </p>
                                </div>
                                {item?.transaction_type === 'INTERBANK' ? (
                                  <div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_name}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('TIN')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.tax_id}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('BIC')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.bank_code}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Document type')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.doc_type}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Document number')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.doc_no}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Document date')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.doc_date}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Narrative')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.narrative}</p>
                                    </div>
                                  </div>
                                ) : null}
                                {item?.transaction_type === 'SWIFT' ? (
                                  <div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_name}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient address')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_address}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient country')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_bank_country}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient city')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_city}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient bank name')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_bank_name}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient bank swift')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_bank_swift}</p>
                                    </div>

                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Narrative')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.narrative}</p>
                                    </div>
                                  </div>
                                ) : null}
                                {item?.transaction_type === 'SWIFT_RUB' ? (
                                  <div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_name}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient address')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_address}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient bank name')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_bank_name}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Recipient bank address')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_bank_address}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('BIC')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.bank_code}</p>
                                    </div>
                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('KPP')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.receiver_kpp}</p>
                                    </div>

                                    <div className='grid grid-cols-6'>
                                      <p className='col-span-2'>{t('Narrative')}: </p>
                                      <p className='ml-3 font-bold col-span-4'>{item?.narrative}</p>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </Panel>
                      ))}
                    </Collapse>
                    <div className='mt-7 flex justify-end'>
                      <Pagination
                        onChange={handlePageChange}
                        current={currentPage}
                        defaultPageSize={10}
                        total={filteredTemplates.length}
                        showSizeChanger={false}
                      />
                    </div>
                  </>
                ) : (
                  <div className='h-24 flex items-center justify-center'>
                    <p>{t('No Templates')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Templates;
